import { FC, ReactNode, useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useUserStore } from "../../../../../stores/user.store";
import { useCurrencySetting } from "../../../../../hooks/useSetting";
import useProgramActivitySummary from "../../../../../hooks/useProgramActivitySummary";
import { useActivePayers } from "../../../../../hooks/userPayers";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import { useAppTheme } from "../../../../../utils/theme";
import {
	NoProgramCard,
	ProgramFilterField,
	ReportTabProps,
	groupLabelsForSites,
	payersForProgram,
	programNameOf,
	programOrgUnitIds,
} from "../common";

interface Option {
	id: string;
	name: string | null;
}

interface Metric {
	label?: string;
	value?: ReactNode;
	color?: string;
	caption?: string;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

const ProgramActivitySummaryTab: FC<ReportTabProps> = ({
	programId,
	programs,
	onProgramChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	const programName = programNameOf(programs, programId);

	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [payerIds, setPayerIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState<string>("");
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");

	// SIMS-7396 / SIMS-7397: payers and group labels are program-specific — clear
	// the selection when the program changes so a stale value isn't sent for a
	// different program.
	useEffect(() => {
		setPayerIds([]);
		setGroupLabelIds([]);
	}, [programId]);

	const { data, isLoading, isFetching } = useProgramActivitySummary({
		accountId,
		programId,
		orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
		payerIds: payerIds.length ? payerIds.join(",") : undefined,
		groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
		providerId: providerId || undefined,
		startDate: startDate || undefined,
		endDate: endDate || undefined,
	});

	const { data: payers } = useActivePayers();
	const { data: groupLabels } = useGroupLabels();
	// SIMS-7397: group labels are scoped to the OUs the user has for the selected
	// program (same logic as the Beneficiary Report): resolve the program's OUs,
	// fetch their provider sites, then keep the labels attached to those sites.
	const groupLabelOuIds = programOrgUnitIds(userInfo?.programs, programId);
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: groupLabelOuIds,
		queryKey: "sims-reports-provider-sites",
	});

	const siteOptions = toOptions(data?.availableSites ?? []);
	const providerOptions = toOptions(data?.availableProviders ?? []);
	// SIMS-7396: the Payer filter lists only payers configured for the selected
	// program (program.payers), intersected with the account's active payers.
	// When the program has none, the dropdown is disabled.
	const selectedProgram = programs.find((p) => p._id === programId);
	const payerOptions = toOptions(payersForProgram(payers, selectedProgram));
	const payersDisabled = payerOptions.length === 0;
	const groupLabelOptions = toOptions(
		groupLabelsForSites(groupLabels, providerSites),
	);

	const money = (n: number): string =>
		`${currencyMask}${(n ?? 0).toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	const counts = data?.enrollmentCounts;
	const inc = data?.incentiveSummary;
	const plural = data?.labels.plural ?? "Members";

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		band: theme.palette.surface.main, // tinted section band
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		good: theme.palette.secondary.dark, // earned / negative / attended
		bad: theme.palette.error.main, // positive / missed / unexcused
		warn: theme.palette.warning.dark, // excused absence
		blue: theme.palette.info.main, // disbursed money
		bandDark: theme.palette.primary.dark, // Clear all button
		onDark: theme.palette.surface.light, // text on the dark button
	};

	const nameById = (opts: Option[], ids: string[]): string =>
		ids
			.map((id) => opts.find((o) => o.id === id)?.name ?? id)
			.filter(Boolean)
			.join(", ");

	// Context subtitle shown under each section band (SIMS-5604) — the applied
	// filters, dot-separated, derived from the current selection.
	const contextLine = [
		programName,
		nameById(siteOptions, orgUnitIds),
		nameById(payerOptions, payerIds),
		startDate || endDate
			? `${startDate ? dayjs(startDate).format("MM/DD/YYYY") : "…"} – ${
					endDate ? dayjs(endDate).format("MM/DD/YYYY") : "…"
				}`
			: "",
	]
		.filter((s) => s && s.length > 0)
		.join(" · ");

	// Semantic numeral color by result type (theme tokens, not raw hex).
	const bucketColor = (key: string, label: string): string => {
		const s = `${key} ${label}`.toLowerCase();
		if (/(unexcused|positive|missed|absent)/.test(s)) return c.bad;
		if (/excus/.test(s)) return c.warn;
		if (/(negative|attend)/.test(s)) return c.good;
		return c.ink;
	};

	// The PDF is the on-screen report captured as-is; the filter bar + buttons
	// are excluded via data-html2canvas-ignore.
	const reportRef = useRef<HTMLDivElement>(null);

	const clearFilters = (): void => {
		// SIMS-7390: a program is always selected — reset to the first assigned
		// program rather than an empty selection.
		onProgramChange(programs[0]?._id ?? "");
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
	};

	const handlePdf = async (): Promise<void> => {
		const el = reportRef.current;
		if (!el) return;
		const canvas = await html2canvas(el, {
			scale: 2,
			useCORS: true,
			backgroundColor: theme.palette.surface.light,
		});
		const img = canvas.toDataURL("image/png");
		const pdf = new jsPDF("portrait", "pt", "a4");
		const pageW = pdf.internal.pageSize.getWidth();
		const pageH = pdf.internal.pageSize.getHeight();
		const imgW = pageW;
		const imgH = (canvas.height * imgW) / canvas.width;
		let heightLeft = imgH;
		let position = 0;
		pdf.addImage(img, "PNG", 0, position, imgW, imgH);
		heightLeft -= pageH;
		while (heightLeft > 0) {
			position -= pageH;
			pdf.addPage();
			pdf.addImage(img, "PNG", 0, position, imgW, imgH);
			heightLeft -= pageH;
		}
		pdf.save("program_summary.pdf");
	};

	// ---- presentational helpers -------------------------------------------
	const FilterField: FC<{
		label: string;
		multi?: boolean;
		children: ReactNode;
	}> = ({ label, multi, children }) => (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 600,
						letterSpacing: "0.06em",
						color: c.label,
					}}
				>
					{label.toUpperCase()}
				</Typography>
				{multi && (
					<Chip
						size="small"
						label="MULTI"
						sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
						color="primary"
						variant="outlined"
					/>
				)}
			</Stack>
			{children}
		</Box>
	);

	const multiInput = (
		value: string[],
		options: Option[],
		onChange: (v: string[]) => void,
		placeholder: string,
		disabled = false,
	): ReactNode => (
		<Select
			size="small"
			multiple
			displayEmpty
			disabled={disabled}
			value={value}
			input={<OutlinedInput />}
			onChange={(e) => {
				const v = e.target.value;
				onChange(typeof v === "string" ? v.split(",") : v);
			}}
			renderValue={(sel) =>
				(sel as string[]).length === 0 ? (
					<Typography sx={{ color: c.muted }}>{placeholder}</Typography>
				) : (
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{(sel as string[]).map((id) => (
							<Chip
								key={id}
								size="small"
								label={options.find((o) => o.id === id)?.name ?? id}
							/>
						))}
					</Box>
				)
			}
		>
			{options.map((o) => (
				<MenuItem key={o.id} value={o.id}>
					{o.name ?? o.id}
				</MenuItem>
			))}
		</Select>
	);

	// Tinted section band: uppercase section title + applied-filter context line.
	const SectionBand: FC<{ title: string }> = ({ title }) => (
		<Box
			sx={{
				bgcolor: c.band,
				px: 3,
				py: 2,
				borderBottom: `1px solid ${c.border}`,
			}}
		>
			<Typography
				sx={{
					fontSize: 13,
					fontWeight: 700,
					letterSpacing: "0.08em",
					color: c.label,
				}}
			>
				{title}
			</Typography>
			{contextLine && (
				<Typography sx={{ fontSize: 13, color: c.muted, mt: 0.5 }}>
					{contextLine}
				</Typography>
			)}
		</Box>
	);

	// 4-column metric row with thin dividers; padded so the grid stays aligned.
	const MetricRow: FC<{ cells: Metric[] }> = ({ cells }) => {
		const padded: Metric[] = [...cells];
		while (padded.length % 4 !== 0) padded.push({});
		return (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
				}}
			>
				{padded.map((cell, i) => (
					<Box
						key={i}
						sx={{
							p: 2.5,
							borderLeft: i % 4 === 0 ? "none" : `1px solid ${c.divider}`,
							borderTop: i >= 4 ? `1px solid ${c.divider}` : "none",
						}}
					>
						{cell.label != null && (
							<>
								<Typography
									sx={{
										fontSize: 14,
										color: c.muted,
										lineHeight: 1.3,
										minHeight: 40,
									}}
								>
									{cell.label}
								</Typography>
								<Typography
									sx={{
										fontSize: 32,
										fontWeight: 700,
										color: cell.color ?? c.ink,
										mt: 0.5,
										lineHeight: 1.1,
									}}
								>
									{cell.value}
								</Typography>
								{cell.caption && (
									<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
										{cell.caption}
									</Typography>
								)}
							</>
						)}
					</Box>
				))}
			</Box>
		);
	};

	const enrollmentCells: Metric[] = [
		{ label: `Number of ${plural} Enrolled`, value: counts?.enrolled ?? 0 },
		{ label: `Number of Active ${plural}`, value: counts?.active ?? 0 },
		{
			label: `${plural} that have Completed the Program`,
			value: counts?.completed ?? 0,
		},
		{
			label: `Number of ${plural} Dis-Enrolled`,
			value: counts?.disenrolled ?? 0,
		},
	];

	const incentiveCells: Metric[] = [
		{
			label: "Total Earned",
			value: money(inc?.totalEarned ?? 0),
			color: c.good,
		},
		{
			label: "Total Earned Transactions",
			value: inc?.earnedTxnCount ?? 0,
			caption: "transactions",
		},
		{
			label: "Total Disbursed",
			value: money(inc?.totalDisbursed ?? 0),
			color: c.blue,
		},
		{
			label: "Total Disbursed Transactions",
			value: inc?.disbursedTxnCount ?? 0,
			caption: "transactions",
		},
	];

	const sectionPaper = {
		borderColor: c.border,
		borderRadius: 3,
		overflow: "hidden",
	} as const;

	return (
		<Stack spacing={3} ref={reportRef}>
			{/* Page header — title + breadcrumb + Export to PDF */}
			<Paper
				variant="outlined"
				sx={{
					borderColor: c.border,
					borderRadius: 3,
					p: 3,
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Box>
					<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
						Program Activity Summary
					</Typography>
					<Typography sx={{ fontSize: 14, color: c.muted, mt: 0.75 }}>
						Reports / Summary
					</Typography>
				</Box>
				<Button
					variant="outlined"
					startIcon={<FileDownloadOutlinedIcon />}
					onClick={handlePdf}
					data-html2canvas-ignore="true"
				>
					Export to PDF
				</Button>
			</Paper>

			{/* Filter bar card (excluded from the PDF capture) */}
			<Paper
				data-html2canvas-ignore="true"
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							md: "repeat(4,1fr) 112px",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programId={programId}
						programs={programs}
						onProgramChange={onProgramChange}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={theme.palette.error.main}
					/>
					<FilterField label="Org units">
						{multiInput(orgUnitIds, siteOptions, setOrgUnitIds, "All sites")}
					</FilterField>
					<FilterField label="Payer" multi>
						{multiInput(
							payerIds,
							payerOptions,
							setPayerIds,
							payersDisabled ? "No payers for this program" : "All payers",
							payersDisabled,
						)}
					</FilterField>
					<FilterField label="Group labels">
						{multiInput(
							groupLabelIds,
							groupLabelOptions,
							setGroupLabelIds,
							"All",
						)}
					</FilterField>
					<FilterField label="Provider">
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) => setProviderId(e.target.value)}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					<FilterField label="Start date">
						<TextField
							size="small"
							type="date"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					<FilterField label="End date">
						<TextField
							size="small"
							type="date"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</FilterField>
					{/* Clear all — 5th column spanning both filter rows (per handoff) */}
					<Button
						variant="contained"
						onClick={clearFilters}
						sx={{
							gridColumn: { xs: "1 / -1", md: "5" },
							gridRow: { md: "1 / span 2" },
							alignSelf: "stretch",
							minHeight: 44,
							bgcolor: c.bandDark,
							color: c.onDark,
							fontWeight: 700,
							letterSpacing: "0.02em",
							"&:hover": { bgcolor: c.bandDark, opacity: 0.92 },
						}}
					>
						Clear all
					</Button>
				</Box>
			</Paper>

			{/* Reserve the bar's height so the content doesn't jump on refetch. */}
			<Box data-html2canvas-ignore="true" sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{!programId && (
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			)}

			{programId && (
				<>
					{/* Enrollment summary */}
					<Paper variant="outlined" sx={sectionPaper}>
						<SectionBand title="ENROLLMENT SUMMARY" />
						<MetricRow cells={enrollmentCells} />
					</Paper>

					{/* Incentive summary */}
					<Paper variant="outlined" sx={sectionPaper}>
						<SectionBand title="INCENTIVE SUMMARY" />
						<MetricRow cells={incentiveCells} />
					</Paper>

					{/* Visit results — one KPI group per configured visit event */}
					<Paper variant="outlined" sx={sectionPaper}>
						<SectionBand title="VISIT RESULTS" />
						{(data?.visitResultsByEvent ?? []).length === 0 ? (
							<Typography sx={{ color: c.muted, px: 3, py: 3 }}>
								No visit results for the selected filters.
							</Typography>
						) : (
							(data?.visitResultsByEvent ?? []).map((ev) => {
								const eventName = ev.eventName ?? "Event";
								return (
									<Box key={ev.eventId}>
										<Box
											sx={{
												bgcolor: c.band,
												px: 3,
												py: 1,
												borderBottom: `1px solid ${c.border}`,
											}}
										>
											<Typography
												sx={{
													fontSize: 12,
													fontWeight: 700,
													letterSpacing: "0.08em",
													color: c.label,
												}}
											>
												{eventName.toUpperCase()}
											</Typography>
										</Box>
										<MetricRow
											cells={ev.buckets.map((b) => ({
												label: `Number of ${b.label} ${eventName}`,
												value: b.count,
												color: bucketColor(b.key, b.label),
											}))}
										/>
									</Box>
								);
							})
						)}
					</Paper>
				</>
			)}
		</Stack>
	);
};

export default ProgramActivitySummaryTab;
